'use client'

import { useRef, useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import {
  EventClickArg,
  DateSelectArg,
  EventInput,
  EventContentArg,
  EventDropArg,
} from '@fullcalendar/core'
import { getEvents, updateEvent } from '@/services/events'
import { CalendarProps, CalendarRef } from './types'
import {
  transformEventsForCalendar,
  filterEventsByMeetupId,
  preloadImages,
  extractImageUrlsFromEvents,
} from './utils/event-processor'
import EventRenderer from './components/EventRenderer'
import './calendar.css'

function Calendar(
  {
    className,
    onEventClick,
    onDateSelect,
    searchFilters,
    meetupId,
    initialView,
    visibleDates,
    hideGlobalEvents = false,
    slotMinTime = '00:00:00',
    slotMaxTime = '24:00:00',
    currentUserId,
  }: CalendarProps,
  ref: React.Ref<CalendarRef>
) {
  const calendarRef = useRef<FullCalendar>(null)
  const [viewMode, setViewMode] = useState<string>(initialView || 'dayGridMonth')
  const [events, setEvents] = useState<EventInput[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dragError, setDragError] = useState<string | null>(null)
  const [preloadedImages, setPreloadedImages] = useState<Set<string>>(new Set())
  const [currentSlotMinTime, setCurrentSlotMinTime] = useState(slotMinTime)
  const [currentSlotMaxTime, setCurrentSlotMaxTime] = useState(slotMaxTime)
  // Add a key to force re-render when time boundaries change
  const [calendarKey, setCalendarKey] = useState(0)

  // Add a ref to track when we're applying a visibleRange restriction
  const isApplyingVisibleRangeRef = useRef(false)

  // Expose the calendar API to the parent component via ref
  useImperativeHandle(ref, () => ({
    getApi: () => {
      return calendarRef.current?.getApi()
    },
    refetchEvents: () => {
      // refetchEvents called via ref
      fetchEvents()
    },
  }))

  // Add a function to validate visibleDates
  const validateVisibleDates = (dates: Date[] | undefined): boolean => {
    if (!dates || dates.length === 0) {
      console.warn('No visible dates provided')
      return false
    }

    // Check if all dates are valid Date objects
    const validDates = dates.filter(date => date instanceof Date && !isNaN(date.getTime()))
    if (validDates.length !== dates.length) {
      console.error('Invalid dates in visibleDates:', dates)
      return false
    }

    // Check if dates are in order
    for (let i = 1; i < dates.length; i++) {
      if (dates[i].getTime() < dates[i - 1].getTime()) {
        console.error('Dates are not in chronological order:', dates)
        return false
      }
    }

    return true
  }

  // Modify the useEffect for visibleDates to include validation
  useEffect(() => {
    if (visibleDates && visibleDates.length > 0 && calendarRef.current) {
      console.log('Applying visible dates restriction:', visibleDates)

      // Validate the dates before proceeding
      if (!validateVisibleDates(visibleDates)) {
        console.error('Invalid visibleDates, skipping restriction')
        return
      }

      const calendarApi = calendarRef.current.getApi()
      if (!calendarApi) {
        console.error('Calendar API not available')
        return
      }

      // Set a ref flag to prevent handling view change events during this update
      isApplyingVisibleRangeRef.current = true

      try {
        // Force the calendar to render in timeGridWeek view for meetup calendars
        if (meetupId && initialView !== 'timeGridDay') {
          console.log('Setting view to timeGridWeek for meetup calendar')
          calendarApi.changeView('timeGridWeek')
          setViewMode('timeGridWeek')
        }

        // If we have visible dates and we're in week view, set the appropriate dates
        if (
          (viewMode === 'timeGridWeek' || initialView === 'timeGridWeek') &&
          visibleDates.length > 0
        ) {
          // Set visible range to the first and last date in the visibleDates array
          const firstDate = visibleDates[0]
          const lastDate = visibleDates[visibleDates.length - 1]

          console.log('Setting visible range:', { firstDate, lastDate })

          // Navigate to the first date
          calendarApi.gotoDate(firstDate)

          // Create a custom week view that only shows the specified dates

          // For FullCalendar, the end date is exclusive, so add 1 day
          const endDate = new Date(lastDate.getTime() + 86400000) // Add one day (86400000 ms)

          // Set view to a duration matching our exact range
          calendarApi.setOption('duration', { days: visibleDates.length })

          // Explicitly set the date range - most important setting
          calendarApi.setOption('visibleRange', {
            start: firstDate,
            end: endDate,
          })

          // Apply strict validRange to prevent navigation outside our dates
          calendarApi.setOption('validRange', {
            start: firstDate,
            end: endDate,
          })

          // Update the dayHeaderFormat to show weekday, month, and day
          calendarApi.setOption('dayHeaderFormat', {
            weekday: 'short',
            month: 'numeric',
            day: 'numeric',
          })

          // Calculate days of week to filter out
          const visibleDaysOfWeek = visibleDates.map(date => date.getDay())
          const allDaysOfWeek = [0, 1, 2, 3, 4, 5, 6] // Sun-Sat
          const hiddenDays = allDaysOfWeek.filter(day => !visibleDaysOfWeek.includes(day))

          // Hide days of the week that aren't in our visible dates
          calendarApi.setOption('hiddenDays', hiddenDays)

          // Force first day to match our first date
          calendarApi.setOption('firstDay', firstDate.getDay())

          // Additional options to restrict view
          calendarApi.setOption('fixedWeekCount', false)
          calendarApi.setOption('showNonCurrentDates', false)

          // Disable navigation buttons to prevent FullCalendar's default behavior
          if (meetupId) {
            calendarApi.setOption('headerToolbar', {
              start: 'title',
              center: '',
              end: 'timeGridWeek', // Only show the current view type
            })
          }

          // Explicitly set the calendar title to our date range
          const startFormatted = firstDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })
          const endFormatted = lastDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
          const newTitle = `${startFormatted} – ${endFormatted}`

          // Use setTimeout to update the title after FullCalendar renders
          setTimeout(() => {
            const titleEl = document.querySelector('.fc-toolbar-title')
            if (titleEl) {
              titleEl.textContent = newTitle
            }
          }, 0)

          console.log('Successfully applied visible dates restriction')
        } else if (viewMode === 'timeGridDay' && visibleDates.length > 0) {
          // For day view, just go to the first date
          calendarApi.gotoDate(visibleDates[0])
        }
      } catch (error) {
        console.error('Error applying visible dates restriction:', error)
      } finally {
        // Reset the flag when done
        isApplyingVisibleRangeRef.current = false
      }
    }
  }, [viewMode, visibleDates, meetupId, initialView])

  // Update time boundaries when slotMinTime or slotMaxTime change
  useEffect(() => {
    // Update state to trigger re-render
    setCurrentSlotMinTime(slotMinTime)
    setCurrentSlotMaxTime(slotMaxTime)

    // Force a re-render of the calendar by changing the key
    setCalendarKey(prevKey => prevKey + 1)

    // Also update the calendar API directly if it's available
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi()
      if (calendarApi) {
        calendarApi.setOption('slotMinTime', slotMinTime)
        calendarApi.setOption('slotMaxTime', slotMaxTime)
      }
    }

    console.warn('Time boundaries:', {
      start: slotMinTime,
      end: slotMaxTime,
    })
  }, [slotMinTime, slotMaxTime])

  // Fetch events from the database
  const fetchEvents = useCallback(async () => {
    try {
      setIsLoading(true)
      // Clear any previous fetch errors, but preserve drag errors
      if (error && !error.includes('creator can reschedule')) {
        setError(null)
      }

      // Get current view's start and end dates
      let startDate, endDate

      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi()
        const view = calendarApi.view
        startDate = new Date(view.activeStart)
        endDate = new Date(view.activeEnd)
      } else {
        // Fallback to current month if calendar not initialized
        const now = new Date()
        startDate = new Date(now.getFullYear(), now.getMonth(), 1)
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      }

      // Fetching events from startDate to endDate

      // Apply search filters if available
      const searchParams: any = {
        startDate,
        endDate,
      }

      if (searchFilters) {
        if (searchFilters.query) searchParams.query = searchFilters.query
        if (searchFilters.startDate) searchParams.startDate = new Date(searchFilters.startDate)
        if (searchFilters.endDate) searchParams.endDate = new Date(searchFilters.endDate)
        if (searchFilters.skillLevel) searchParams.skillLevel = searchFilters.skillLevel
        if (searchFilters.isPrivate !== undefined) searchParams.isPrivate = searchFilters.isPrivate
        if (searchFilters.hostId) searchParams.hostId = searchFilters.hostId
        if (searchFilters.gameId) searchParams.gameId = searchFilters.gameId
      }

      // Add meetup ID to search params if provided
      if (meetupId) {
        searchParams.meetupId = meetupId
      }
      // If hideGlobalEvents is true, we ONLY want events for this meetup
      // If it's false, we'll show all events or filter by meetupId if provided
      else if (hideGlobalEvents) {
        // If we're hiding global events but no meetupId is provided, show no events
        setEvents([])
        setIsLoading(false)
        return
      }

      // Call getEvents with the date objects directly
      const { data, error: fetchError } = await getEvents(searchParams)

      if (fetchError) {
        console.error('Error details:', fetchError)
        throw new Error(fetchError)
      }

      if (data) {
        // If hideGlobalEvents is true and we have a meetupId, filter to only show
        // events for this specific meetup
        let filteredEvents = data
        if (hideGlobalEvents && meetupId) {
          filteredEvents = filterEventsByMeetupId(data, meetupId)
        }

        // Transform database events to FullCalendar format
        const calendarEvents = transformEventsForCalendar(filteredEvents)
        setEvents(calendarEvents)
      }
    } catch (err) {
      console.error('Error fetching events:', err)
      // Only update error state for fetch errors, not for drag errors
      setError('Failed to connect to server. Please refresh and try again.')
    } finally {
      setIsLoading(false)
    }
  }, [searchFilters, meetupId, hideGlobalEvents, error])

  // Initial fetch and setup refresh event listener
  useEffect(() => {
    fetchEvents()

    // Listen for refresh events from parent components
    const handleRefresh = () => {
      // Calendar refresh event received
      fetchEvents()
    }

    window.addEventListener('calendar:refresh', handleRefresh)

    return () => {
      window.removeEventListener('calendar:refresh', handleRefresh)
    }
  }, [fetchEvents])

  // Handle window resize for responsive adjustments
  const handleWindowResize = useCallback(() => {
    // Adjust calendar height based on view and window size
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi()
      const isMobile = window.innerWidth < 768

      // Set appropriate height based on device and view
      if (isMobile) {
        calendarApi.setOption('height', 'auto')
        // On mobile, simplify the header toolbar
        calendarApi.setOption('headerToolbar', {
          left: 'prev,next',
          center: 'title',
          right: 'dayGridMonth,timeGridDay',
        })
      } else {
        calendarApi.setOption('height', 'auto')
        // On desktop, show full header toolbar
        calendarApi.setOption('headerToolbar', {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        })
      }
    }
  }, [])

  // Handle event rendering customization
  const renderEventContent = (eventInfo: EventContentArg) => {
    return <EventRenderer eventInfo={eventInfo} calendarApi={calendarRef.current?.getApi()} />
  }

  // Handle event click
  const handleEventClick = useCallback(
    (clickInfo: EventClickArg) => {
      if (onEventClick) {
        onEventClick(clickInfo)
      } else {
        // Default behavior if no custom handler provided
        alert(
          `Event: ${clickInfo.event.title}\n${clickInfo.event.extendedProps?.description || ''}`
        )
      }
    },
    [onEventClick]
  )

  // Handle date selection
  const handleDateSelect = useCallback(
    (selectInfo: DateSelectArg) => {
      if (onDateSelect) {
        onDateSelect(selectInfo)
      } else {
        // Default behavior if no custom handler provided
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi()
          calendarApi.unselect() // Clear date selection
        }
      }
    },
    [onDateSelect]
  )

  // Add a view change handler to prevent view changes outside our range
  const handleViewChange = useCallback(
    (arg: { view: any }) => {
      // Skip if we're in the middle of applying our restrictions
      if (isApplyingVisibleRangeRef.current) {
        return
      }

      // If a meetup is specified and we have visible dates, enforce our view
      if (meetupId && visibleDates && visibleDates.length > 0 && calendarRef.current) {
        const api = calendarRef.current.getApi()

        // Check if the current view's time range matches our visible dates
        const currentStartTime = api.view.currentStart.getTime()
        const firstVisibleDate = visibleDates[0]

        // If the start date has drifted, force it back
        if (Math.abs(currentStartTime - firstVisibleDate.getTime()) > 43200000) {
          // 12 hours tolerance
          console.log('[CALENDAR] View changed to incorrect date, forcing back to meetup range')
          api.gotoDate(firstVisibleDate)
        }
      }
    },
    [meetupId, visibleDates]
  )

  // Custom buttons for additional navigation control
  const customButtons = {
    myToday: {
      text: 'Today',
      click: () => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi()
          calendarApi.today()
        }
      },
    },
  }

  // After setting events, preload the images
  useEffect(() => {
    if (events.length > 0) {
      const imageUrls = extractImageUrlsFromEvents(events)
      const updatedPreloadedImages = preloadImages(imageUrls, preloadedImages)
      setPreloadedImages(updatedPreloadedImages)
    }
  }, [events, preloadedImages])

  // Handle event drop (reschedule)
  const handleEventDrop = useCallback(
    (dropInfo: EventDropArg) => {
      const { event } = dropInfo
      const hostId = event.extendedProps?.host_id

      // Debug the IDs to see what's happening
      console.log('Event Drop Debug:', {
        hostId,
        currentUserId,
        eventTitle: event.title,
        hostIdType: typeof hostId,
        currentUserIdType: typeof currentUserId,
        extendedProps: event.extendedProps,
      })

      // If there's no current user ID, user is not logged in
      if (!currentUserId) {
        // Revert the change
        dropInfo.revert()

        // Show an error message for unauthenticated users
        setDragError('Please log in to reschedule events.')

        // Clear error after a few seconds
        setTimeout(() => setDragError(null), 3000)
        return
      }

      // If the current user is not the host, they can't edit
      // Convert both to strings for comparison to avoid type mismatches
      if (String(hostId) !== String(currentUserId)) {
        // Revert the change
        dropInfo.revert()

        // Show an error message for unauthorized users
        setDragError('Only the event creator can reschedule events.')

        // Clear error after a few seconds
        setTimeout(() => setDragError(null), 3000)
        return
      }

      // User is the creator, proceed with update
      const eventId = event.id
      const newStart = event.start
      const newEnd = event.end

      if (!eventId || !newStart) return

      // Update the event with new start and end times
      updateEvent(eventId, {
        start_time: newStart.toISOString(),
        end_time: newEnd ? newEnd.toISOString() : undefined,
      }).catch(err => {
        console.error('Error updating event:', err)
        // Revert the change if the update fails
        dropInfo.revert()
        setDragError('Failed to update event time.')
        setTimeout(() => setDragError(null), 3000)
      })
    },
    [currentUserId]
  )

  // Force correct date range to be applied on initial render and window focus
  useEffect(() => {
    if (!visibleDates || visibleDates.length === 0 || !calendarRef.current) {
      return
    }

    let applyTimeout: NodeJS.Timeout | null = null

    // Function to force correct date range
    const applyCorrectDateRange = () => {
      if (!calendarRef.current) return

      const api = calendarRef.current.getApi()
      if (!api) return

      const firstDate = visibleDates[0]
      const lastDate = visibleDates[visibleDates.length - 1]

      // Log current state before making changes
      console.log('[CALENDAR FORCE DATE RANGE] Before applying:', {
        currentView: api.view.type,
        currentStart: api.view.currentStart?.toISOString(),
        targetStart: firstDate.toISOString(),
      })

      // Force our constraints
      try {
        // Mark that we're applying changes to prevent view change handlers
        isApplyingVisibleRangeRef.current = true

        // Apply all constraints in sequence
        api.gotoDate(firstDate)

        api.setOption('validRange', {
          start: firstDate,
          end: new Date(lastDate.getTime() + 86400000),
        })

        api.setOption('visibleRange', {
          start: firstDate,
          end: new Date(lastDate.getTime() + 86400000),
        })

        // Calculate days of week to show (for timeGrid views)
        if (api.view.type.includes('timeGrid')) {
          const visibleDaysOfWeek = visibleDates.map(date => date.getDay())
          const allDaysOfWeek = [0, 1, 2, 3, 4, 5, 6] // Sun-Sat
          const hiddenDays = allDaysOfWeek.filter(day => !visibleDaysOfWeek.includes(day))

          api.setOption('hiddenDays', hiddenDays)
          api.setOption('firstDay', firstDate.getDay())
        }

        // Log result
        console.log('[CALENDAR FORCE DATE RANGE] After applying:', {
          updatedView: api.view.type,
          updatedStart: api.view.currentStart?.toISOString(),
          updatedEnd: api.view.currentEnd?.toISOString(),
        })
      } catch (err) {
        console.error('[CALENDAR FORCE DATE RANGE] Error:', err)
      } finally {
        isApplyingVisibleRangeRef.current = false
      }
    }

    // Apply immediately
    applyTimeout = setTimeout(applyCorrectDateRange, 0)

    // Also apply when window gets focus, to handle cases where user switches tabs
    const handleFocus = () => {
      console.log('[CALENDAR] Window focused, reapplying date range')
      applyCorrectDateRange()
    }

    window.addEventListener('focus', handleFocus)

    // Also reapply on window resize, as layout changes can sometimes trigger view changes
    const handleResize = () => {
      if (applyTimeout) {
        clearTimeout(applyTimeout)
      }
      applyTimeout = setTimeout(() => {
        console.log('[CALENDAR] Window resized, reapplying date range')
        applyCorrectDateRange()
      }, 100)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      if (applyTimeout) {
        clearTimeout(applyTimeout)
      }
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('resize', handleResize)
    }
  }, [visibleDates])

  return (
    <div className={`calendar ${className || ''}`}>
      {/* Skip rendering the calendar until visibleDates are loaded if a meetupId is provided */}
      {(!meetupId || (meetupId && visibleDates && visibleDates.length > 0)) && (
        <>
          <div className="calendar-controls mb-4 flex justify-end">
            {/* Navigation buttons would go here */}
          </div>

          <div className="relative">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
                <div className="loading-spinner" />
              </div>
            )}

            <FullCalendar
              key={calendarKey}
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={viewMode}
              // Force initial date to the first visible date
              initialDate={visibleDates && visibleDates.length > 0 ? visibleDates[0] : undefined}
              // Use validRange to restrict navigation
              validRange={
                meetupId && visibleDates && visibleDates.length > 0
                  ? {
                      start: visibleDates[0],
                      end: new Date(visibleDates[visibleDates.length - 1].getTime() + 86400000),
                    }
                  : undefined
              }
              // Set visible range to force the correct date range
              visibleRange={
                meetupId && visibleDates && visibleDates.length > 0
                  ? {
                      start: visibleDates[0],
                      end: new Date(visibleDates[visibleDates.length - 1].getTime() + 86400000),
                    }
                  : undefined
              }
              // Fix the duration for week view
              duration={
                meetupId && visibleDates && visibleDates.length > 0 && viewMode.includes('timeGrid')
                  ? { days: visibleDates.length }
                  : undefined
              }
              // Set which days of week to display
              hiddenDays={
                meetupId && visibleDates && visibleDates.length > 0 && viewMode.includes('timeGrid')
                  ? (() => {
                      const visibleDaysOfWeek = visibleDates.map(date => date.getDay())
                      const allDaysOfWeek = [0, 1, 2, 3, 4, 5, 6] // Sun-Sat
                      return allDaysOfWeek.filter(day => !visibleDaysOfWeek.includes(day))
                    })()
                  : undefined
              }
              // Set first day of week to match our first date
              firstDay={
                meetupId && visibleDates && visibleDates.length > 0
                  ? visibleDates[0].getDay()
                  : undefined
              }
              // Simplify header for meetup calendars
              headerToolbar={
                meetupId && visibleDates && visibleDates.length > 0
                  ? {
                      start: 'title',
                      center: '',
                      end: viewMode, // Only show current view type
                    }
                  : {
                      start: 'prev,next today',
                      center: 'title',
                      end: 'dayGridMonth,timeGridWeek,timeGridDay',
                    }
              }
              timeZone="local"
              height="auto"
              eventDisplay="block"
              events={events}
              slotMinTime={currentSlotMinTime}
              slotMaxTime={currentSlotMaxTime}
              select={onDateSelect}
              eventClick={onEventClick}
              eventContent={renderEventContent}
              editable={false} // Set true to enable drag and drop
              selectable={true}
              selectMirror={true}
              dayMaxEvents={true}
              weekends={true}
              eventDrop={handleEventDrop}
              nowIndicator={true}
              showNonCurrentDates={!meetupId}
              fixedWeekCount={!meetupId}
            />
          </div>
        </>
      )}

      {error && <div className="text-red-500 mt-4">{error}</div>}
      {dragError && <div className="text-orange-500 mt-4">{dragError}</div>}
    </div>
  )
}

export default forwardRef(Calendar)
